
import { Language } from '@/types/insurance';

// Add language enforcement settings
const DEFAULT_LANGUAGE = 'de';
const ALLOWED_LANGUAGES = ['de', 'fr', 'it', 'en', 'es', 'pt', 'nl', 'pl', 'ru', 'tr', 'ar', 'hi', 'zh', 'ja', 'ko'] as const;

export function enforceLanguage(detectedOrRequestedLanguage?: string): string {
  // Always default to German
  if (!detectedOrRequestedLanguage) {
    return DEFAULT_LANGUAGE;
  }
  
  const normalizedLanguage = detectedOrRequestedLanguage.toLowerCase();
  
  if (ALLOWED_LANGUAGES.includes(normalizedLanguage as typeof ALLOWED_LANGUAGES[number])) {
    return normalizedLanguage;
  }
  
  return DEFAULT_LANGUAGE;
}

export function detectLanguage(text: string): string {
  if (!text || typeof text !== 'string') {
    return enforceLanguage();
  }

  const languagePatterns = {
    de: [
      /\b(ein|eine|der|die|das|und|ist|für|mit|zu|von|auf|ja|nein|hallo|bitte|danke)\b/i,
      /\b(wie|warum|wenn|aber|oder|nicht|wir|mein|dein|sehr|viel|haben|sein)\b/i,
      /\b(guten tag|guten morgen|guten abend|versicherung|vertrag|angebot)\b/i,
      /ß|ä|ö|ü/i,
    ],
    fr: [
      /\b(le|la|les|un|une|des|et|est|pour|avec|à|de|en|bonjour|merci|oui|non)\b/i,
      /\b(je|tu|il|elle|nous|vous|ils|elles|suis|es|est|sommes|êtes|sont)\b/i,
      /\b(assurance|contrat|offre|client|conseil)\b/i,
    ],
    it: [
      /\b(il|lo|la|i|gli|le|un|una|e|è|per|con|a|di|in|ciao|grazie|sì|no)\b/i,
      /\b(io|tu|lui|lei|noi|voi|loro|sono|sei|è|siamo|siete|sono)\b/i,
      /\b(assicurazione|contratto|offerta|cliente|consulenza)\b/i,
    ],
    en: [
      /\b(the|a|an|and|is|for|with|to|from|on|yes|no|hello|please|thank|you)\b/i,
      /\b(how|why|when|but|or|not|we|my|your|very|much|have|be)\b/i,
      /\b(good morning|good evening|insurance|contract|offer)\b/i,
    ],
    es: [
      /\b(el|la|los|las|un|una|unos|unas|y|es|para|con|a|de|en|hola|gracias|sí|no)\b/i,
      /\b(yo|tú|él|ella|nosotros|vosotros|ellos|ellas|soy|eres|es|somos|sois|son)\b/i,
      /\b(seguro|contrato|oferta|cliente|consulta)\b/i,
    ],
  };

  const matches = Object.entries(languagePatterns).map(([lang, patterns]) => {
    let count = 0;
    patterns.forEach(pattern => {
      if (pattern.test(text)) {
        count++;
      }
    });
    return { lang, count };
  });

  const bestMatch = matches.reduce((prev, curr) => 
    curr.count > prev.count ? curr : prev
  );

  return bestMatch.count > 0 ? enforceLanguage(bestMatch.lang) : enforceLanguage();
}

export function getInitialGreeting(language: string): string {
  // Always default to German for consistency
  return "Willkommen! 👋 Ich bin Ihr E-Mail-Assistent. Lassen Sie uns gemeinsam die perfekte E-Mail-Vorlage erstellen.\n\n" +
    "Um Ihnen bestmöglich zu helfen, erzählen Sie mir bitte:\n" +
    "1. Den Zweck Ihrer E-Mail (z.B. Newsletter, Angebot, Beratungstermin)\n" +
    "2. Die wichtigsten Punkte, die Sie kommunizieren möchten\n" +
    "3. Den gewünschten Ton (formell, persönlich, professionell)\n\n" +
    "Oder wählen Sie eine Kategorie aus den folgenden Vorlagen:";
}

export function getTemplatePrompt(category: string, language: string = 'de'): string {
  const enforced = enforceLanguage(language);
  
  const prompts: Record<string, Record<string, string>> = {
    newsletter: {
      de: 'Erstellen Sie eine professionelle Newsletter-Vorlage für Versicherungskunden mit aktuellen Updates und wertvollen Tipps.',
      fr: 'Créez un modèle de newsletter professionnel pour les clients d\'assurance avec des mises à jour et des conseils précieux.',
      it: 'Crea un modello di newsletter professionale per i clienti assicurativi con aggiornamenti e consigli utili.',
      en: 'Create a professional newsletter template for insurance customers with current updates and valuable tips.',
      es: 'Crea una plantilla de newsletter profesional para clientes de seguros con actualizaciones y consejos valiosos.',
    },
    offer: {
      de: 'Entwickeln Sie eine überzeugende E-Mail-Vorlage für ein personalisiertes Versicherungsangebot.',
      fr: 'Développez un modèle d\'e-mail convaincant pour une offre d\'assurance personnalisée.',
      it: 'Sviluppa un modello email convincente per un\'offerta assicurativa personalizzata.',
      en: 'Develop a compelling email template for a personalized insurance offer.',
      es: 'Desarrolla una plantilla de correo electrónico convincente para una oferta de seguro personalizada.',
    },
    welcome: {
      de: 'Gestalten Sie eine herzliche Willkommens-E-Mail für neue Versicherungskunden.',
      fr: 'Créez un e-mail de bienvenue chaleureux pour les nouveaux clients d\'assurance.',
      it: 'Crea un\'email di benvenuto calorosa per i nuovi clienti assicurativi.',
      en: 'Create a warm welcome email for new insurance customers.',
      es: 'Crea un correo electrónico de bienvenida cálido para nuevos clientes de seguros.',
    },
    consulting: {
      de: 'Erstellen Sie eine E-Mail-Vorlage für die Vereinbarung eines Beratungsgesprächs.',
      fr: 'Créez un modèle d\'e-mail pour la prise de rendez-vous de consultation.',
      it: 'Crea un modello email per fissare un appuntamento di consulenza.',
      en: 'Create an email template for scheduling a consultation meeting.',
      es: 'Crea una plantilla de correo electrónico para programar una reunión de consultoría.',
    },
  };

  return prompts[category]?.[enforced] || prompts[category]?.['de'] || 'Erstellen Sie eine professionelle E-Mail-Vorlage.';
}
